import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from './api'

export type PlanningDashboard = {
  academicSession: string
  totalPlans: number
  draft: number
  pending: number
  published: number
  deficitSubjects: number
  alerts: Array<{ planId: string; gap: number; message: string }>
}

export type AcademicPlan = {
  _id: string
  subjectConfigId: string
  syllabusVersionId: string
  capabbleClassKey: string
  section?: string
  subjectName: string
  status: string
  revisionNumber: number
  allocationStrategy: string
  revisionPeriods: number
  assessmentBufferPeriods: number
}

export type PlanSubjectOption = {
  _id: string
  subjectName: string
  capabbleClassKey: string
  activeSyllabusVersionId?: string
  syllabusStatus: string | null
}

export type CapacitySnapshot = {
  periodsPerWeek: number
  workingDays: number
  effectiveAvailablePeriods: number
  totalRequiredPeriods: number
  capacityGap: number
  projectedCompletionDate: string
  explanation: string
}

export type MatrixRow = {
  topicId: string
  title: string
  estimatedPeriods?: number | null
  byMonth: Record<string, number>
  totalPlanned: number
  status: string
}

export type PlanningMatrix = {
  plan: AcademicPlan
  months: string[]
  matrix: MatrixRow[]
  capacity: CapacitySnapshot | null
  monthlyTotals: Record<string, number>
}

const base = '/acdmc/planning'

export const fetchPlanningDashboard = () =>
  api.get<{ success: boolean; data: PlanningDashboard }>(`${base}/dashboard`).then((r) => r.data.data)

export const fetchPlans = () =>
  api.get<{ success: boolean; data: AcademicPlan[] }>(`${base}/plans`).then((r) => r.data.data)

export const fetchPlanSubjects = () =>
  api.get<{ success: boolean; data: PlanSubjectOption[] }>(`${base}/subjects`).then((r) => r.data.data)

export const fetchPlanningMatrix = (planId: string) =>
  api.get<{ success: boolean; data: PlanningMatrix }>(`${base}/plans/${planId}/matrix`).then((r) => r.data.data)

export const createPlan = (body: { subjectConfigId: string; section?: string; allocationStrategy?: string }) =>
  api.post(`${base}/plans`, body).then((r) => r.data)

export const calculatePlanCapacity = (planId: string) =>
  api.post(`${base}/plans/${planId}/capacity`).then((r) => r.data)

export const generatePlanAllocation = (planId: string, strategy?: string) =>
  api.post(`${base}/plans/${planId}/allocation/generate`, strategy ? { strategy } : {}).then((r) => r.data)

export const transitionPlan = (planId: string, status: string, comment?: string) =>
  api.post(`${base}/plans/${planId}/transition`, { status, comment }).then((r) => r.data)

export const createPlanScenario = (
  planId: string,
  body: { name?: string; inputs?: Record<string, number> },
) => api.post(`${base}/plans/${planId}/scenarios`, body).then((r) => r.data)

export function usePlanningDashboard() {
  return useQuery({ queryKey: ['acdmc', 'planning', 'dashboard'], queryFn: fetchPlanningDashboard })
}

export function usePlans() {
  return useQuery({ queryKey: ['acdmc', 'planning', 'plans'], queryFn: fetchPlans })
}

export function usePlanSubjects() {
  return useQuery({ queryKey: ['acdmc', 'planning', 'subjects'], queryFn: fetchPlanSubjects })
}

export function usePlanningMatrix(planId: string | null) {
  return useQuery({
    queryKey: ['acdmc', 'planning', 'matrix', planId],
    queryFn: () => fetchPlanningMatrix(planId!),
    enabled: Boolean(planId),
  })
}

export function useCreatePlan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createPlan,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['acdmc', 'planning'] })
    },
  })
}

export function usePlanCapacity(planId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => calculatePlanCapacity(planId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['acdmc', 'planning', 'matrix', planId] })
      qc.invalidateQueries({ queryKey: ['acdmc', 'planning', 'dashboard'] })
    },
  })
}

export function useGenerateAllocation(planId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (strategy?: string) => generatePlanAllocation(planId, strategy),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['acdmc', 'planning', 'matrix', planId] })
    },
  })
}
